import React from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import useTheme from '@material-ui/core/styles/useTheme'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Link from '@material-ui/core/Link'
import CloseIcon from '@material-ui/icons/Close'
import ZoomInIcon from '@material-ui/icons/ZoomIn'
import ZoomOutIcon from '@material-ui/icons/ZoomOut'
import RotateLeftIcon from '@material-ui/icons/RotateLeft'
import RotateRightIcon from '@material-ui/icons/RotateRight'
import NewCardBuilder from '../NewCardBuilder'
import CheckIcon from '@material-ui/icons/Check'
import BlockIcon from '@material-ui/icons/Block'
import Box from '@material-ui/core/Box'
import ApplyAllCheckbox from '../Common/ApplyAllCheckbox'
import { CircularProgress, Hidden } from '@material-ui/core'
import Alert from "@material-ui/lab/Alert";

const useStyles = makeStyles((theme) => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
        zIndex: 1,
    },
    imageIcon: {
        fontSize: '3rem',
    },
    usePhotoButton: {
        textTransform: 'none',
        marginBottom: '1rem',
    },
    uploadPhotoButton: {
        textTransform: 'none',
    },
    linkText: {
        textDecorationLine: 'underline',
        color: theme.palette.secondary.main,
        fontWeight: 'bold',
    },
    approvalHeadingText: {
        marginBottom: '0.5rem',
        color: 'green',
        fontSize: '1rem',
        fontWeight: 'bold',
        textAlign: 'left',
    },
    rejectionHeadingText: {
        marginBottom: '0.5rem',
        color: 'red',
        fontSize: '1rem',
        fontWeight: 'bold',
        textAlign: 'left',
    },
    lineItemIcon: {
        marginRight: '1rem',
        color: 'green',
    },
    rejectionLineItemIcon: {
        marginRight: '1rem',
        color: 'red',
    },
    lineItemText: {
        fontSize: '1rem',
        textAlign: 'left',
    },
    lineItemCorporateText: {
        textAlign: 'left',
        marginLeft: '2.5rem',
        fontSize: '0.9rem',
        fontStyle: 'italic',
        color: '#595959',
    },
    iconsContainer: {
        marginTop: '1rem',
        marginBottom: '1rem',
    },
    useThisPhotoLoading: {
        color: theme.palette.primary.main,
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    alertMessageContainer: {
        marginBottom: '1.25rem',
    },
}))

const PhotoLineItem = ({ lineItemText, rejection = false }) => {
    const classes = useStyles()

    return (
        <Box display={'flex'}>
            {!rejection ? (
                <CheckIcon className={classes.approvalLineItemIcon} />
            ) : (
                <BlockIcon className={classes.rejectionLineItemIcon} />
            )}

            <Typography className={classes.lineItemText}>
                {lineItemText}
            </Typography>
        </Box>
    )
}

const CardBuilderModal = (props) => {
    const {
        applyCardImageToAll,
        setApplyCardImageToAll,
        showApplyAllCheckbox,
        activeCardIndexText,
        showActiveCardIndexText,
        uploadingImageToCloudinary,
        imageRulesResultIsLoading,
        hasAnyImageViolations,
        isImageBlocked = () => false,
        imageRulesCheckResult
    } = props
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const violations = {
        alcohol: 'May contain alcohol',
        cartoon: 'May contain a cartoon',
        celebrity: 'May contain a celebrity',
        copyrighted_material: 'May contain copyrighted material',
        firearms: 'May contain firearms',
        gambling: 'May contain gambling',
        nudity: 'May contain nudity',
        numbers: 'May contain numbers',
        texts: 'May contain texts',
        tobacco: 'May contain tobacco',
        violence: 'May contain violence',
    }

    const renderPhotoButtons = (hideMobile, hideDesktop) => (
        <Hidden xsDown={hideMobile} smUp={hideDesktop}>
            <Box style={{ width: '100%', marginTop: '2rem' }}>
                {showApplyAllCheckbox && (
                    <Box>
                        <ApplyAllCheckbox
                            applyToAll={applyCardImageToAll}
                            setApplyToAll={setApplyCardImageToAll}
                            labelText={
                                'Use this image for remaining gift cards'
                            }
                            name={'image'}
                        />
                    </Box>
                )}
                {props.imageFile && !isImageBlocked() && <Button
                    id="confirm-photo-selection"
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth={true}
                    className={classes.usePhotoButton}
                    onClick={props.saveDesignAssets}
                    disabled={uploadingImageToCloudinary || imageRulesResultIsLoading}
                >
                    Use this photo
                    {(uploadingImageToCloudinary || imageRulesResultIsLoading) && (
                        <CircularProgress
                            size={24}
                            className={classes.useThisPhotoLoading}
                        />
                    )}
                </Button>}
                <Button
                    variant={'outlined'}
                    component="label"
                    color="primary"
                    size={'large'}
                    fullWidth={true}
                    className={classes.uploadPhotoButton}
                    onClick={props.openImageInput}
                >
                    {hasAnyImageViolations() ? 'Change' : `Upload ${props.imageFile && ' a different'}`} photo
                </Button>
            </Box>
        </Hidden>
    )

    const renderImageRulesCheckResult = () => {
        const legacyItems = Object.entries(imageRulesCheckResult)
            .filter(([key, value]) => value === true)
            .map(([key]) => (
                <PhotoLineItem
                    key={key}
                    rejection={true}
                    lineItemText={violations[key]}
                />
            ))

        const imageCheckerV2Items = (imageRulesCheckResult?.imageCheckerV2?.findings ?? [])
            .map((finding) => (
                <PhotoLineItem
                    key={`v2-${finding.rule_key}`}
                    rejection={true}
                    lineItemText={`May contain: ${finding.customer_wording}`}
                />
            ))

        return [...legacyItems, ...imageCheckerV2Items]
    }

    return (
        <React.Fragment>
            <Dialog
                id="card-builder-modal"
                fullWidth={true}
                fullScreen={isMobile}
                maxWidth={'lg'}
                open={props.modalIsOpen}
                onClose={props.closeModal}
                aria-labelledby="Card Builder"
            >
                <DialogTitle id="max-width-dialog-title">
                    <IconButton
                        data-testid="close-button"
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={props.closeModal}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Grid
                        container
                        spacing={2}
                        style={{
                            marginBottom: '1rem',
                            paddingBottom: '2rem',
                        }}
                    >
                        <Grid
                            item
                            xs={12}
                            sm={7}
                            style={{
                                display: (props.imageFile || !isMobile) ? 'flex' : 'none',
                                visibility: props.imageFile ? 'visible' : 'hidden',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                marginBottom: '1.5rem',
                                paddingLeft: '1rem',
                                paddingRight: '1rem',
                            }}
                        >
                            {showActiveCardIndexText && (
                                <Typography
                                    data-testid="select-image-card-index-text"
                                    style={{ fontWeight: 'bold' }}
                                >
                                    {activeCardIndexText}
                                </Typography>
                            )}
                            <NewCardBuilder
                                imageFile={props.imageFile}
                                setCropper={props.setCropper}
                                hasAnyImageViolations={hasAnyImageViolations}
                            />
                            <Grid
                                container
                                spacing={2}
                                justify="center"
                                alignItems="center"
                                className={classes.iconsContainer}
                                style={{
                                    display: (isMobile && hasAnyImageViolations()) ? 'none' : 'flex',
                                    pointerEvents: !hasAnyImageViolations() ? 'auto' : 'none'
                                }}
                            >
                                <IconButton
                                    variant={'contained'}
                                    onClick={props.zoomInBackground}
                                    color="primary"
                                >
                                    <ZoomInIcon
                                        fontSize="large"
                                        className={classes.imageIcon}
                                    />
                                </IconButton>
                                <IconButton
                                    variant={'contained'}
                                    onClick={props.zoomOutBackground}
                                    color="primary"
                                >
                                    <ZoomOutIcon
                                        fontSize="large"
                                        className={classes.imageIcon}
                                    />
                                </IconButton>
                                <IconButton
                                    variant={'contained'}
                                    onClick={props.rotateLeft}
                                    color="primary"
                                >
                                    <RotateLeftIcon
                                        fontSize="large"
                                        className={classes.imageIcon}
                                    />
                                </IconButton>
                                <IconButton
                                    variant={'contained'}
                                    onClick={props.rotateRight}
                                    color="primary"
                                >
                                    <RotateRightIcon
                                        fontSize="large"
                                        className={classes.imageIcon}
                                    />
                                </IconButton>
                            </Grid>
                            {renderPhotoButtons(false, true)}
                            <Typography
                                style={{
                                    display: (isMobile && hasAnyImageViolations()) ? 'none' : 'unset',
                                    fontSize: '1rem',
                                    textAlign: 'center',
                                    marginTop: '1rem',
                                    marginBottom: '1rem',
                                    width: '100%',
                                }}
                            >
                                Move the preview box to select your gift card
                                background.
                                <br />
                                Photos must adhere to our{' '}
                                <Link
                                    href={`${props.imageGuidelinesUrl}`}
                                    className={classes.linkText}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    image guidelines
                                </Link>{' '}
                                provided by Visa and Mastercard.
                            </Typography>
                            <Typography
                                style={{
                                    display: (isMobile && hasAnyImageViolations()) ? 'none' : 'unset',
                                    textAlign: 'center',
                                    marginTop: '1rem',
                                    marginBottom: '1rem',
                                    width: '100%',
                                }}
                            >
                                <strong>
                                    Looking to add a business logo or owned
                                    protected image to your card?
                                </strong>
                                <br />
                                Visit our{' '}
                                <Link
                                    href={`${props.baseUrl}/gift-cards-for-business/`}
                                    target="_blank"
                                    className={classes.linkText}
                                >
                                    Business Page
                                </Link>{' '}
                                to order Co-Branded Reward cards in bulk!
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sm={5}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                paddingLeft: '1rem',
                                paddingRight: '1rem',
                            }}
                        >
                            <p
                                style={{
                                    fontSize: '24px',
                                    fontWeight: 'bold',
                                    margin: '0',
                                    textAlign: 'center',
                                    marginBottom: '1rem',
                                }}
                            >
                                {props.imageFile ? 'Adjust' : 'Upload'} your photo
                            </p>
                            {!hasAnyImageViolations() && <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    marginBottom: '1rem',
                                }}
                            >
                                <Typography
                                    className={classes.approvalHeadingText}
                                >
                                    Your photo will be approved if:
                                </Typography>
                                <PhotoLineItem
                                    lineItemText={
                                        'It is a personal photo you own the rights to'
                                    }
                                />
                                <PhotoLineItem
                                    lineItemText={'It is less than 10MB'}
                                />
                                <PhotoLineItem
                                    lineItemText={
                                        'It does not contain copyrighted material'
                                    }
                                />
                                <PhotoLineItem
                                    lineItemText={
                                        'It does not contain offensive, violent, or illegal material'
                                    }
                                />
                            </Box>}
                            {hasAnyImageViolations() && <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    marginBottom: '1rem',
                                }}
                            >
                                {isImageBlocked() ? (
                                    <Alert
                                        severity="error"
                                        className={`image-content-alert ${classes.alertMessageContainer}`}
                                    >
                                        The image you uploaded does not meet our image guidelines.
                                        Please choose a different image.
                                    </Alert>
                                ) : (
                                    <Alert
                                        severity="warning"
                                        className={`image-content-alert ${classes.alertMessageContainer}`}
                                    >
                                        The image you uploaded may violate our image guidelines.
                                        You can continue with your purchase but your order may be declined later.
                                    </Alert>
                                )}
                                <Typography style={{color: '#DC2626', fontWeight: 'bold'}}>
                                    Possible Image Violation(s)
                                </Typography>
                                <Typography className={`image-content-alert-reasons`}>
                                    {renderImageRulesCheckResult()}
                                </Typography>
                            </Box>}
                            {renderPhotoButtons(!!props.imageFile, false)}
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    )
}

export default CardBuilderModal
